import 'react-swipeable-list/dist/styles.css';

import {
  Box,
  Button,
  chakra,
  DrawerCloseButton,
  Flex,
  Icon,
  Link,
  Text,
} from '@chakra-ui/react';
import Notification from '@ui/components/Notification';
import type { HintType } from '@ui/hooks/useShowHowItWorks';
import useShowHowItWorks from '@ui/hooks/useShowHowItWorks';
import { useTranslations } from '@ui/i18n';
import { Trash } from '@veno-app/icons';
import { useState } from 'react';
import {
  SwipeableList as SwipeableList_,
  SwipeableListItem,
  SwipeAction,
  TrailingActions,
  Type,
} from 'react-swipeable-list';

import { InDesktop, InMobile } from '../MobileOrDesktop';
import {
  ModalOrDragDrawerContent,
  ModalOrDrawer,
  ModalOrDrawerBodyWithMask,
  ModalOrDrawerFooter,
  ModalOrDrawerHeader,
  ModalOrDrawerOverlay,
} from '../ModalOrDrawer/ModalOrDrawerParts';

const SwipeableList = chakra(SwipeableList_);

interface HowItWorksDrawerProps {
  hintType: HintType;
  title: string;
  shortText: string;
  shortTextDesktop?: JSX.Element;
  longText: JSX.Element;
  modalHeight?: string;
  transparentOverlayHeightPx?: number;
}

const HowItWorksDrawer = ({
  hintType,
  title,
  shortText,
  shortTextDesktop,
  longText,
}: HowItWorksDrawerProps) => {
  const t = useTranslations();
  const [dismissed, setDismissed] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const closeDrawer = () => setDrawerOpen(false);
  const [showHowItWorks] = useShowHowItWorks(hintType);

  const [hiddenElementOpacity, setHiddenElementOpacity] = useState(0);

  const notificationNode = (
    <Notification
      mx={{
        base: '16px',
        desktop: '0',
      }}
      mt="10px"
      mb={{ base: '27px', desktop: '10px' }}
      flexGrow={{ base: 1, desktop: 0 }}
      p={{ base: '10px', desktop: '24px' }}
      h={{ base: '83px', desktop: 'auto' }}
      flexShrink={0}
      textWidth={{ base: '248px', desktop: 'none' }}
      button={
        <Button
          size={{ base: 'sm', desktop: 'md' }}
          onClick={() => setDrawerOpen(true)}
        >
          {t('View')}
        </Button>
      }
      title={t('How does it work?')}
      text={shortText}
      textDesktop={shortTextDesktop}
    />
  );

  return (
    <>
      {showHowItWorks && !dismissed && (
        <InDesktop>{notificationNode}</InDesktop>
      )}
      {showHowItWorks && !dismissed && (
        <InMobile>
          <SwipeableList flex={0} type={Type.IOS}>
            <SwipeableListItem
              trailingActions={
                <TrailingActions>
                  <SwipeAction onClick={() => {}}>
                    <Button
                      onClick={() => setDismissed(true)}
                      m="10px"
                      variant="raised"
                      w="87px"
                      h="83px"
                      overflow="hidden"
                      opacity={hiddenElementOpacity}
                    >
                      <Flex flexDir="column" align="center">
                        <Box>
                          <Icon as={Trash} width="24px" height="24px" />
                        </Box>
                        <Text color="#000000">Dismiss</Text>
                      </Flex>
                    </Button>
                  </SwipeAction>
                </TrailingActions>
              }
              onSwipeProgress={(progress) => {
                setHiddenElementOpacity((progress * 3) / 100);
              }}
              onSwipeStart={() => {
                setHiddenElementOpacity(0);
              }}
            >
              {notificationNode}
            </SwipeableListItem>
          </SwipeableList>
        </InMobile>
      )}
      <ModalOrDrawer
        isOpen={drawerOpen}
        onClose={closeDrawer}
        scrollBehavior="inside"
        placement="bottom"
        size={{
          base: 'full',
          desktop: 'md',
        }}
      >
        <ModalOrDrawerOverlay />
        <ModalOrDragDrawerContent>
          <ModalOrDrawerHeader>{t('How it works')}</ModalOrDrawerHeader>
          <InDesktop>
            <DrawerCloseButton />
          </InDesktop>
          <ModalOrDrawerBodyWithMask>
            <Text textAlign="center" mb="40px" textStyle="h3">
              {title}
            </Text>
            <Box textStyle="body" color="text-light">
              {longText}
            </Box>
          </ModalOrDrawerBodyWithMask>
          <ModalOrDrawerFooter>
            <HowItWorksFooter close={closeDrawer} hintType={hintType} />
          </ModalOrDrawerFooter>
        </ModalOrDragDrawerContent>
      </ModalOrDrawer>
    </>
  );
};

const HowItWorksFooter = ({
  close,
  hintType,
}: {
  close: () => void;
  hintType: HintType;
}) => {
  const t = useTranslations();
  const [, setShowHowItWorks] = useShowHowItWorks(hintType);

  return (
    <>
      <Button onClick={close}>{t('Close')}</Button>
      <Box textAlign="center" pt="20px">
        <Link
          variant="secondary"
          onClick={() => {
            close();
            setShowHowItWorks(false);
          }}
          textAlign="center"
        >
          {t("Don't show it again")}
        </Link>
      </Box>
    </>
  );
};

export default HowItWorksDrawer;
